import React, { useEffect, useState, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../store/hooks';

import MyTextInput from '../components/form/MyTextInput';
import { ROUTES } from '../resources/routes-constants';
import { APIS_ENDPOINTS, APIS_METHOD } from '../resources/api-constants';
import { request } from '../utility/apiClients';
import { renderCustomCloseButton, getValue } from '../utility/helpers';
import { logo } from '../assets/images/images';
import { getUserProfile } from '../store/features/auth';

interface UserProfile {
  id: number;
  user: {
    id: number;
    full_name: string;
    email: string;
    is_active: boolean;
    profile_status: number;
    user_type: number;
    phone_number: string;
  };
  office_address: {
    id: number;
    address: string;
    latitude: string;
    longitude: string;
    zipcode: string;
    city: {
      id: number;
      name: string;
    };
    state: {
      id: number;
      name: string;
    };
    country: {
      id: number;
      name: string;
    };
  };
  industry_type: {
    id: number;
    name: string;
  };
  annual_revenue: {
    id: number;
    name: string;
  };
  logo: string | null;
  website_link: string;
  company_size: {
    id: number;
    name: string;
  };
  company_name: string;
  conversation_id: string;
  tier_assigned: {
    id: number;
    name: string;
    assigned_users: number;
    no_of_downloads: number;
    no_of_job_creation: number;
    no_of_profile_views: number;
    mask: boolean;
  };
  job_left: boolean;
}

interface ContactFormData {
  full_name: string;
  email: string;
  phone_number: string;
  company_name: string;
  website_link: string;
  company_size: string;
  industry_type: string;
  annual_revenue: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  current_plan: string;
  message: string;
}

const ContactUsToUpgradePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async () => {
    try {
      const companyId = getValue('companyId');
      if (!companyId) {
        toast.error('Company ID not found. Please log in again.', { closeButton: renderCustomCloseButton() });
        navigate('/');
        return;
      }

      const payload = {
        companyId,
        loader: false
      };
      
      const res = await dispatch(getUserProfile(payload)).unwrap();
      
      if (res?.data) {
        setUserProfile(res.data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load user profile', { closeButton: renderCustomCloseButton() });
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    // Only fetch once on mount
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    full_name: Yup.string().required('Full name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone_number: Yup.string().required('Phone number is required'),
    company_name: Yup.string().required('Company name is required'),
    website_link: Yup.string().test('website-url', 'Invalid website URL', function(value) {
      if (!value || value.trim() === '') return true; // Allow empty values
      
      const trimmedValue = value.trim();
      // Check if it's a valid URL (with or without protocol)
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      return urlPattern.test(trimmedValue);
    }),
    company_size: Yup.string().required('Company size is required'),
    industry_type: Yup.string().required('Industry type is required'),
    annual_revenue: Yup.string().required('Annual revenue is required'),
    address: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    zipcode: Yup.string().required('Zipcode is required'),
    message: Yup.string().required('Message is required'),
  });

  const handleSubmit = async (values: ContactFormData) => {
    try {
      // Format website URL to ensure it has https://
      let formattedWebsite = values.website_link;
      if (formattedWebsite && formattedWebsite.trim()) {
        formattedWebsite = formattedWebsite.trim();
        if (!formattedWebsite.startsWith('http://') && !formattedWebsite.startsWith('https://')) {
          formattedWebsite = `https://${formattedWebsite}`;
        }
      }

      // Send the form data to the enhanced contact sales API
      const response = await request(
        APIS_METHOD.POST,
        APIS_ENDPOINTS.ENHANCED_CONTACT_SALES,
        {
          full_name: values.full_name,
          email: values.email,
          phone_number: values.phone_number,
          company_name: values.company_name,
          website_link: formattedWebsite,
          company_size: values.company_size,
          industry_type: values.industry_type,
          annual_revenue: values.annual_revenue,
          address: values.address,
          city: values.city,
          state: values.state,
          country: values.country,
          zipcode: values.zipcode,
          current_plan: values.current_plan,
          message: values.message,
        }
      );
      
      if (response.data) {
        toast.success('Your upgrade request has been submitted successfully! Our team will contact you soon.', { 
          closeButton: renderCustomCloseButton() 
        });
        
        // Navigate back to dashboard
        navigate(ROUTES.DASHBOARD_PAGE_ROUTE);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit your request. Please try again.', { 
        closeButton: renderCustomCloseButton() 
      });
    }
  };

  if (loading) {
    return (
      <div className="contact-upgrade-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading your profile information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Format website URL for display (remove https:// if present for user editing)
  const formatWebsiteForDisplay = (website: string) => {
    if (!website) return '';
    return website.replace(/^https?:\/\//, '');
  };

  const initialValues: ContactFormData = {
    full_name: userProfile?.user?.full_name || '',
    email: userProfile?.user?.email || '',
    phone_number: userProfile?.user?.phone_number || '',
    company_name: userProfile?.company_name || '',
    website_link: formatWebsiteForDisplay(userProfile?.website_link || ''),
    company_size: userProfile?.company_size?.name || '',
    industry_type: userProfile?.industry_type?.name || '',
    annual_revenue: userProfile?.annual_revenue?.name || '',
    address: userProfile?.office_address?.address || '',
    city: userProfile?.office_address?.city?.name || '',
    state: userProfile?.office_address?.state?.name || '',
    country: userProfile?.office_address?.country?.name || '',
    zipcode: userProfile?.office_address?.zipcode || '',
    current_plan: userProfile?.tier_assigned?.name || '',
    message: '',
  };

  return (
    <div className="contact-upgrade-page">
      <div className="container">
        <div className="contact-upgrade-header">
          <img src={logo} alt="Logo" className="brand-logo" onClick={() => navigate(ROUTES.HOMEPAGE_ROUTE)} />
          <h1>Contact Us to Upgrade</h1>
          <p>Fill out the form below and our team will contact you to discuss upgrade options.</p>
        </div>

        <div className="contact-upgrade-form-container">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form className="contact-upgrade-form">
                <div className="form-section">
                  <h3>Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Full Name *"
                        name="full_name"
                        type="text"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="Email Address *"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Phone Number *"
                        name="phone_number"
                        type="tel"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="Current Plan"
                        name="current_plan"
                        type="text"
                        disabled
                        placeholder="Current plan"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Company Information</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Company Name *"
                        name="company_name"
                        type="text"
                        placeholder="Enter company name"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="Website"
                        name="website_link"
                        type="text"
                        placeholder="example.com (https:// will be added automatically)"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Company Size *"
                        name="company_size"
                        type="text"
                        placeholder="Enter company size"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="Industry Type *"
                        name="industry_type"
                        type="text"
                        placeholder="Enter industry type"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Annual Revenue *"
                        name="annual_revenue"
                        type="text"
                        placeholder="Enter annual revenue"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Office Address</h3>
                  <div className="form-row">
                    <div className="form-group full-width">
                      <MyTextInput
                        label="Address *"
                        name="address"
                        type="text"
                        placeholder="Enter office address"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="City *"
                        name="city"
                        type="text"
                        placeholder="Enter city"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="State *"
                        name="state"
                        type="text"
                        placeholder="Enter state"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <MyTextInput
                        label="Country *"
                        name="country"
                        type="text"
                        placeholder="Enter country"
                      />
                    </div>
                    <div className="form-group">
                      <MyTextInput
                        label="Zipcode *"
                        name="zipcode"
                        type="text"
                        placeholder="Enter zipcode"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>Upgrade Request</h3>
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label htmlFor="message">Message *</label>
                      <Field
                        as="textarea"
                        id="message"
                        name="message"
                        className="form-control"
                        rows={4}
                        placeholder="Tell us about your upgrade requirements..."
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                    className="btn-submit"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => navigate(ROUTES.DASHBOARD_PAGE_ROUTE)}
                    className="contact-upgrade-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ContactUsToUpgradePage;
